import React, { useCallback, useRef, useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { useDashboardStore } from '../../store/dashboardStore';
import { Staff, NewStaffData } from '../../types';
import { Loader2 } from 'lucide-react';
import { getStaffDeletePreview } from '../../lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';

const RequiredMark = () => <span className="text-red-600">※必須</span>;

type StaffRequiredHighlightKey = 'name' | 'nameRomaji' | 'pin';
type StaffConfirmAction = 'reset_pin';

interface StaffFormPanelProps {
  isMobile?: boolean;
}

export const StaffFormPanel: React.FC<StaffFormPanelProps> = ({ isMobile = false }) => {
  const { 
    editingStaff, 
    addStaff, 
    updateStaff,
    deleteStaff,
    currentUser,
    allStaff,
    isUpdatingStaff,
    isAddingStaff,
    isDeletingStaff,
    staffFormRevision,
    isNavigatingBack,
    navigateBackToStaffList,
    currentClinicId,
    isDeviceUser,
  } = useDashboardStore();
  const isEditing = !!editingStaff;
  const isSelf = !!(isEditing && editingStaff && currentUser?.id === editingStaff.id);
  const canResetOtherPin = !!(isEditing && !isSelf && (currentUser?.role === 'admin' || currentUser?.role === 'developer'));
  const isLoading = isUpdatingStaff || isAddingStaff || isDeletingStaff;

  // 最後の管理者かどうかを判定
  const adminCount = allStaff.filter(s => s.role === 'admin' || s.role === 'developer').length;
  const isLastAdmin = isEditing && (editingStaff?.role === 'admin' || editingStaff?.role === 'developer') && adminCount === 1;

  // フォームの各入力値をstateで管理
  const [name, setName] = useState('');
  const [nameRomaji, setNameRomaji] = useState('');
  const [pin, setPin] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [staffFormError, setStaffFormError] = useState<string | null>(null);
  const [staffFormNotice, setStaffFormNotice] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<StaffConfirmAction | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePreviewCount, setDeletePreviewCount] = useState(0);
  const [deleteReplacementStaffId, setDeleteReplacementStaffId] = useState('');
  const [isDeletePreviewLoading, setIsDeletePreviewLoading] = useState(false);
  const [requiredFieldHighlight, setRequiredFieldHighlight] = useState<Set<StaffRequiredHighlightKey>>(
    () => new Set()
  );

  const pinRequired = !isEditing || isSelf;
  const isRequiredInvalid = (key: StaffRequiredHighlightKey) => requiredFieldHighlight.has(key);
  const requiredLabelClass = (key: StaffRequiredHighlightKey) =>
    `block text-sm font-medium ${isRequiredInvalid(key) ? 'text-red-700' : 'text-gray-700'}`;
  const requiredInputClass = (key: StaffRequiredHighlightKey) =>
    `mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
      isRequiredInvalid(key) ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
    }`;

  const clearStaffFormValidation = () => {
    setStaffFormError(null);
    setStaffFormNotice(null);
    setRequiredFieldHighlight(new Set<StaffRequiredHighlightKey>());
  };

  useEffect(() => {
    clearStaffFormValidation();
    if (editingStaff) {
      setName(editingStaff.name || '');
      setNameRomaji(editingStaff.nameRomaji || '');
      setPin(editingStaff.pin || '');
      setIsAdmin(editingStaff.role === 'admin' || editingStaff.role === 'developer');
      setShowDeleteDialog(false);
      setDeletePreviewCount(0);
      setDeleteReplacementStaffId('');
      setIsDeletePreviewLoading(false);
    } else {
      // 新規登録モードの時はフォームを空にする
      setName('');
      setNameRomaji('');
      setPin('');
      setIsAdmin(false);
      setShowDeleteDialog(false);
      setDeletePreviewCount(0);
      setDeleteReplacementStaffId('');
      setIsDeletePreviewLoading(false);
    }
  }, [editingStaff]);

  // 【重要】「保存する」ボタンがクリックされた時の処理
  const handleSave = async () => {
    const nameTrimmed = name.trim();
    const nameRomajiTrimmed = nameRomaji.trim();
    const pinDigits = pinRequired ? String(pin ?? '').replace(/\D/g, '').slice(0, 4) : '';

    const missing = new Set<StaffRequiredHighlightKey>();
    if (!nameTrimmed) missing.add('name');
    if (!nameRomajiTrimmed) missing.add('nameRomaji');
    if (pinRequired && !pinDigits) missing.add('pin');

    if (missing.size > 0) {
      setRequiredFieldHighlight(new Set(missing));
      setStaffFormError('必須項目が未入力です。');
      return;
    }

    if (pinRequired) {
      if (!/^\d{4}$/.test(pinDigits)) {
        setRequiredFieldHighlight(new Set<StaffRequiredHighlightKey>(['pin']));
        setStaffFormError('PINコードは4桁の数字で入力してください。');
        return;
      }
      if (pinDigits === '0000') {
        setRequiredFieldHighlight(new Set<StaffRequiredHighlightKey>(['pin']));
        setStaffFormError('PINコード「0000」は設定できません。');
        return;
      }
    }

    const duplicatePinStaff = allStaff.find(s => {
      if (!pinRequired) return false;
      if (!s.isActive) return false;
      if (!s.pin) return false;
      if (isEditing && editingStaff && s.id === editingStaff.id) {
        return false;
      }
      return String(s.pin) === String(pinDigits);
    });
    if (duplicatePinStaff) {
      setRequiredFieldHighlight(new Set<StaffRequiredHighlightKey>(['pin']));
      setStaffFormError(
        `このPINは既に使用されています（${duplicatePinStaff.name}さん）。別のPINを設定してください。`
      );
      return;
    }

    clearStaffFormValidation();

    const currentUserId = currentUser?.id || 'Unknown'; // 追加: currentUserIdを取得

    if (isEditing && editingStaff) {
      // 編集モード：更新データを準備
      const updatedData: Partial<Staff> = {
        name: nameTrimmed,
        nameRomaji: nameRomajiTrimmed,
        // PINは本人のみ変更可能。管理者は「リセット」操作でのみ変更する。
        ...(isSelf ? { pin: pinDigits } : {}),
        role: (isAdmin ? 'admin' : 'staff') as 'admin' | 'staff',
        updatedBy: currentUserId,
      };
      
      // ストアの更新関数を呼び出し、処理が終わるのを待つ
      await updateStaff(editingStaff.id, updatedData);
      
    } else {
      // 新規登録モード：新規データを準備
      const newStaffData: NewStaffData = {
        name: nameTrimmed,
        nameRomaji: nameRomajiTrimmed,
        pin: pinDigits,
        phoneNumber: '',
        role: (isAdmin ? 'admin' : 'staff'),
        jobTitle: '',
      };
      
      try {
        const newStaff = await addStaff(newStaffData);
        
        if (newStaff) {
          setShowAddSuccess(true);
        } else {
          setStaffFormError('スタッフの登録に失敗しました。時間をおいて再度お試しください。');
        }
      } catch (error) {
        console.error('スタッフ追加エラー:', error);
        setStaffFormError('スタッフの登録中にエラーが発生しました。時間をおいて再度お試しください。');
      }
    }
  };

  const generateUniquePin = (): string => {
    const normalizePin = (p: string): string | null => {
      const s = String(p ?? '').replace(/\D/g, '').slice(0, 4);
      if (!/^\d{4}$/.test(s)) return null;
      if (s === '0000') return null;
      return s;
    };

    const usedPins = new Set(
      allStaff
        .filter((s) => s.isActive && s.id !== editingStaff?.id)
        .map((s) => String(s.pin ?? '').trim())
        .filter((p) => p.length > 0)
    );

    for (let i = 0; i < 50; i++) {
      const candidate = normalizePin(String(Math.floor(Math.random() * 10_000)).padStart(4, '0'));
      if (candidate && !usedPins.has(candidate)) return candidate;
    }
    return '9999';
  };

  const executeResetPin = async () => {
    if (!editingStaff) return;

    const nextPin = generateUniquePin();
    const success = await updateStaff(editingStaff.id, { pin: nextPin });
    if (success) {
      setStaffFormNotice(`PINをリセットしました。新しいPIN: ${nextPin}`);
    } else {
      setStaffFormError('PINのリセットに失敗しました。');
    }
  };

  const handleResetPin = () => {
    clearStaffFormValidation();
    if (!editingStaff) return;
    if (!canResetOtherPin) return;
    setConfirmAction('reset_pin');
  };

  const handleOpenDeleteDialog = async () => {
    clearStaffFormValidation();
    if (!editingStaff || !currentClinicId) return;

    setDeleteReplacementStaffId('');
    setIsDeletePreviewLoading(true);
    const preview = await getStaffDeletePreview({
      clinicId: currentClinicId,
      staffId: editingStaff.id,
      actedStaffId: isDeviceUser ? (currentUser?.id ?? undefined) : undefined,
    });
    setIsDeletePreviewLoading(false);

    if (!preview) {
      setStaffFormError('削除前の確認に失敗しました。');
      return;
    }

    setDeletePreviewCount(preview.futureAppointmentsCount);
    setShowDeleteDialog(true);
  };

  const executeDeleteStaff = async (options?: {
    replacementStaffId?: string;
    allowUnassigned?: boolean;
  }) => {
    if (!editingStaff) return;

    const result = await deleteStaff(editingStaff.id, options);
    if (!result.success) {
      setStaffFormError(result.error || 'スタッフの削除に失敗しました。');
      setShowDeleteDialog(false);
      return;
    }

    setShowDeleteDialog(false);
    // The panel will be closed by the store action on success
  };

  const handleConfirmAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;
    if (action === 'reset_pin') {
      await executeResetPin();
      return;
    }
  };

  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const handleCloseAddSuccess = () => {
    setShowAddSuccess(false);
    navigateBackToStaffList();
  };

  const deleteReplacementCandidates = allStaff.filter(
    (staff) => staff.isActive && staff.id !== editingStaff?.id
  );
  const confirmDialogCopy: Record<StaffConfirmAction, { title: string; description: string; actionLabel: string }> = {
    reset_pin: {
      title: 'PINを再発行しますか？',
      description: `「${editingStaff?.name ?? ''}」さんのPINをリセットします。`,
      actionLabel: '再発行する',
    },
  };
  const activeConfirmDialog = confirmAction ? confirmDialogCopy[confirmAction] : null;

  const staffFormSwipeRef = useRef<HTMLDivElement>(null);
  const handleStaffFormEdgeBack = useCallback(() => {
    navigateBackToStaffList();
  }, [navigateBackToStaffList]);
  const { swipeStyle: staffFormSwipeStyle } = useMobileEdgeSwipeBack(staffFormSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile && !showAddSuccess,
    onNavigateBack: handleStaffFormEdgeBack,
  });

  return (
    <>
      {/* スタッフ追加完了ポップアップ（予約登録と同様） */}
      {showAddSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={handleCloseAddSuccess}>
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2 text-gray-800">
              <p className="font-semibold text-green-600">スタッフ追加完了</p>
            </div>
            <Button type="button" variant="cta" className="mt-4 w-full" onClick={handleCloseAddSuccess}>
              OK
            </Button>
          </div>
        </div>
      )}
    <div
      ref={staffFormSwipeRef}
      style={isMobile ? staffFormSwipeStyle : undefined}
      className={isMobile ? 'fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-50' : 'contents'}
    >
    <motion.div
      variants={panelVariants}
      initial={isMobile && isNavigatingBack ? "hiddenBack" : "hidden"}
      animate="visible"
      exit={
        isMobile
          ? isNavigatingBack
            ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
            : { x: '-100%' }
          : undefined
      }
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? "h-full w-full bg-white flex flex-col shadow-lg" 
        : "panel-col panel-col--settings z-20 bg-white border-r flex flex-col shadow-lg relative"
      }
    >
      {/* ▼▼▼ ローディングオーバーレイ ▼▼▼ */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
        </div>
      )}
      {/* ▲▲▲ ローディングオーバーレイ ▲▲▲ */}
      
      <div className="flex items-center justify-between p-4 border-b relative z-20 flex-shrink-0">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                navigateBackToStaffList();
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              {isEditing ? `スタッフ情報編集` : '新規スタッフ登録'}
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">
              {isEditing ? `スタッフ情報編集` : '新規スタッフ登録'}
            </h2>
          )}
        </div>
      </div>

      <motion.div
        key={staffFormRevision}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={`flex-1 overflow-y-auto p-6 space-y-6 ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}
      >
        <div>
          <label htmlFor="name" className={requiredLabelClass('name')}>
            名前 <RequiredMark />
          </label>
          <input
            type="text"
            id="name"
            placeholder="例）田中太郎"
            value={name}
            onChange={(e) => {
              clearStaffFormValidation();
              setName(e.target.value);
            }}
            className={requiredInputClass('name')}
          />
        </div>
        <div>
          <label htmlFor="nameRomaji" className={requiredLabelClass('nameRomaji')}>
            ローマ字（検索用） <RequiredMark />
          </label>
          <input
            type="text"
            id="nameRomaji"
            placeholder="例）tanaka taro"
            value={nameRomaji}
            onChange={(e) => {
              clearStaffFormValidation();
              setNameRomaji(e.target.value);
            }}
            className={requiredInputClass('nameRomaji')}
          />
        </div>
        <div>
          <label
            htmlFor="pin"
            className={pinRequired ? requiredLabelClass('pin') : 'block text-sm font-medium text-gray-700'}
          >
            PINコード {pinRequired && <RequiredMark />}
          </label>
          <input
            type="text"
            id="pin"
            placeholder="例）1234"
            value={isEditing && !isSelf ? '••••' : pin}
            onChange={(e) => {
              clearStaffFormValidation();
              setPin(e.target.value);
            }}
            maxLength={4}
            pattern="[0-9]*"
            inputMode="numeric"
            autoComplete="off"
            disabled={isEditing && !isSelf}
            className={
              isEditing && !isSelf
                ? 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed'
                : requiredInputClass('pin')
            }
          />
          {isEditing && !isSelf ? (
            <p className="text-xs text-gray-500 mt-1">
              PINは本人のみ変更できます。管理者はリセット（再発行）できます。
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              4桁の数字を入力してください。
            </p>
          )}
          {canResetOtherPin && (
            <Button
              type="button"
              variant="cta"
              onClick={handleResetPin}
              className="mt-2 w-full"
              disabled={isLoading}
            >
              PINをリセット（再発行）
            </Button>
          )}
        </div>
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => {
                clearStaffFormValidation();
                setIsAdmin(e.target.checked);
              }}
              disabled={isLastAdmin}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-base font-medium text-gray-700">管理者権限</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {isLastAdmin 
              ? '最後の管理者のため、権限は外せません。' 
              : 'チェックすると管理者権限が付与されます'
            }
          </p>
        </div>

                 {/* 履歴表示エリア - 編集モードの場合のみ表示 */}
         {isEditing && editingStaff && (
           <div className="mt-6 p-4 bg-gray-700 rounded-lg">
             <h3 className="text-sm font-medium text-white mb-2">履歴情報</h3>
             <div className="space-y-2 text-xs text-white">
               <div>
                 <span className="font-medium">作成日時:</span> {new Date(editingStaff.createdAt).toLocaleString('ja-JP')}
               </div>
               <div>
                <span className="font-medium">作成者:</span> {editingStaff.createdByName || allStaff.find(s => s.id === editingStaff.createdBy)?.name || editingStaff.createdBy || '不明'}
               </div>
               <div>
                 <span className="font-medium">最終更新日時:</span> {new Date(editingStaff.updatedAt).toLocaleString('ja-JP')}
               </div>
               <div>
                <span className="font-medium">最終更新者:</span> {editingStaff.updatedByName || allStaff.find(s => s.id === editingStaff.updatedBy)?.name || editingStaff.updatedBy || '不明'}
               </div>
             </div>
           </div>
         )}

        {staffFormError && (
          <p className="text-sm text-red-600" role="alert">
            {staffFormError}
          </p>
        )}
        {staffFormNotice && (
          <p className="text-sm text-green-600" role="status">
            {staffFormNotice}
          </p>
        )}

        {/* 保存・キャンセルボタン */}
        <div className="flex justify-end space-x-3 mt-6">
          {/* 
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            閉じる
          </button>
           */}
          {/* 編集モードの場合のみ削除ボタンを表示 */}
          {isEditing && (
            <Button
              type="button"
              onClick={() => {
                void handleOpenDeleteDialog();
              }}
              disabled={isLoading || isDeletePreviewLoading || isLastAdmin || currentUser?.id === editingStaff?.id}
              className="bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:text-white"
            >
              削除する
            </Button>
          )}
          <Button type="button" variant="cta" onClick={handleSave}>
            {isEditing ? '更新する' : '保存する'}
          </Button>
        </div>
      </motion.div>
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{activeConfirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>{activeConfirmDialog?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleConfirmAction();
              }}
              disabled={isLoading}
              className="bg-teal-600 text-white hover:bg-teal-700"
            >
              {isLoading ? <Loader2 className="animate-spin h-4 w-4" /> : activeConfirmDialog?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>本当に削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {deletePreviewCount > 0
                ? `「${editingStaff?.name ?? ''}」には未来の予約が ${deletePreviewCount} 件あります。別の医師へ付け替えて削除するか、そのまま削除してください。`
                : `「${editingStaff?.name ?? ''}」を削除します。この操作は元に戻せません。`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deletePreviewCount > 0 && (
            <div className="space-y-3">
              <div>
                <label htmlFor="deleteReplacementStaff" className="block text-sm font-medium text-gray-700">
                  付け替え先の医師
                </label>
                <select
                  id="deleteReplacementStaff"
                  value={deleteReplacementStaffId}
                  onChange={(e) => setDeleteReplacementStaffId(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={isLoading || isDeletePreviewLoading}
                >
                  <option value="">選択してください</option>
                  {deleteReplacementCandidates.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500">
                「そのまま削除」を選ぶと、未来の予約は残りますが担当者は未設定になります。
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading || isDeletePreviewLoading}>キャンセル</AlertDialogCancel>
            {deletePreviewCount > 0 && (
              <Button
                type="button"
                variant="outline"
                disabled={isLoading || isDeletePreviewLoading}
                onClick={() => {
                  void executeDeleteStaff({ allowUnassigned: true });
                }}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                {isLoading ? <Loader2 className="animate-spin h-4 w-4" /> : 'そのまま削除'}
              </Button>
            )}
            <Button
              type="button"
              disabled={
                isLoading ||
                isDeletePreviewLoading ||
                (deletePreviewCount > 0 && !deleteReplacementStaffId)
              }
              onClick={() => {
                void executeDeleteStaff(
                  deletePreviewCount > 0
                    ? { replacementStaffId: deleteReplacementStaffId }
                    : undefined
                );
              }}
              className="bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:text-white"
            >
              {isLoading ? (
                <Loader2 className="animate-spin h-4 w-4" />
              ) : deletePreviewCount > 0 ? (
                '付け替えて削除'
              ) : (
                '削除する'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
    </div>
    </>
  );
};
